import React, { useState, useEffect } from "react";
import { ArrowRight, Check, ChevronDown, ChevronUp, Upload, Link as LinkIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  submitAuditIntake, getAuditJob, getBenchmarkCategories,
  extractIntakeFinancials, extractShippingPolicy,
} from "@/lib/api";
import EvidencePacks from "@/components/EvidencePacks";
import { COUNTRIES } from "@/lib/markets";

const PNL_FORMATS = ["Xero export", "QuickBooks export", "MYOB export", "Spreadsheet", "Not sure yet"];
const CURRENCIES = ["AUD", "USD", "INR", "GBP", "EUR", "NZD", "CAD", "SGD"];

// Where the business trades. Asked outright, and never derived from the
// currency beside it - a brand can bill in AUD and operate from Singapore.
// Margin Lens's benchmarks and cost tolerances describe Australian online-only
// brands; applying them to anyone else produces a confident answer about the
// wrong economy, which is exactly what happened to a real buyer in August.
// Leaving this blank is a valid answer and means "not stated", which the audit
// treats as a reason to withhold every category comparison - not as Australia.
const OTHER = "__other__";

// Backend extraction returns pnl_currency; the form field for the same value is "currency".
const EXTRACT_TO_FORM_KEY = { pnl_currency: "currency" };

const FIELDS = {
  business_name: "", contact_email: "", store_domain: "",
  business_category: "", business_subtype: "",
  pnl_format: "", shipping_model: "", primary_question: "",
  client_country: "", currency: "", reporting_period_months: "", monthly_gross_revenue: "", monthly_order_volume: "",
  aov: "", cogs_pct_of_aov: "", returns_restocked: "1", category_mix_notes: "",
  return_rate_pct: "", cost_per_return: "", onward_shipping_cost: "", packaging_cost: "",
  payment_gateway_fee_pct: "", fulfillment_3pl_fee: "",
  monthly_marketing_spend: "", paid_attributed_revenue_pct: "", repeat_purchase_rate_pct: "", blended_roas: "",
  avg_orders_per_customer_12m: "",
  sells_on_marketplaces: false, marketplace_names: "", marketplace_revenue_share_pct: "",
  marketplace_commission_pct: "", marketplace_ad_spend: "",
  monthly_fixed_overhead: "", overhead_salaries: "", overhead_rent: "", overhead_software: "",
  overhead_other: "", amortized_capex_notes: "",
  return_policy_url: "", shipping_policy_url: "", warranty_or_guarantee_url: "",
  brand_positioning_notes: "", target_customer_notes: "",
};

const NUMERIC_FIELDS = [
  "reporting_period_months", "monthly_gross_revenue", "monthly_order_volume",
  "aov", "cogs_pct_of_aov",
  "return_rate_pct", "cost_per_return", "onward_shipping_cost", "packaging_cost",
  "payment_gateway_fee_pct", "fulfillment_3pl_fee",
  "monthly_marketing_spend", "paid_attributed_revenue_pct", "repeat_purchase_rate_pct", "blended_roas",
  "avg_orders_per_customer_12m",
  "marketplace_revenue_share_pct", "marketplace_commission_pct", "marketplace_ad_spend",
  "monthly_fixed_overhead", "overhead_salaries", "overhead_rent", "overhead_software", "overhead_other",
];

function Field({ label, hint, children, required, highlight }) {
  const ring = highlight === "auto" ? "ring-1 ring-[#3f6b4a]/50 rounded-[6px]"
    : highlight === "missing" ? "ring-1 ring-[#9a3412]/50 rounded-[6px]" : "";
  return (
    <div>
      <label className="tick block mb-2">
        {label}
        {required && <span className="text-[#9a3412]"> *</span>}
        {highlight === "auto" && <span className="ml-2 lowercase tracking-normal text-[#3f6b4a]">— auto-filled, verify</span>}
        {highlight === "missing" && <span className="ml-2 lowercase tracking-normal text-[#9a3412]">— not found in file</span>}
      </label>
      <div className={ring}>{children}</div>
      {hint && <p className="mt-1.5 font-mono-tech text-[11px] text-[#525252]">{hint}</p>}
    </div>
  );
}

function GroupHeader({ tag, title, subtitle }) {
  return (
    <div className="pt-2">
      <div className="tick text-[#9a3412] mb-1">{tag}</div>
      <div className="text-[#171717] font-mono-tech text-[13px] font-medium">{title}</div>
      {subtitle && <p className="mt-1 font-mono-tech text-[11px] text-[#525252] max-w-lg">{subtitle}</p>}
    </div>
  );
}

/**
 * Post-purchase intake for the Margin Audit.
 *
 * Fields follow the contribution-margin waterfall a real margin audit
 * reconciles against: CM1 (unit economics) -> CM2 (fulfillment & returns) ->
 * CM3 (marketing) -> CM4 (marketplace, optional) -> fixed overhead. Every
 * financial field beyond the basics is optional - most buyers won't have
 * every number to hand on day one, and a gap belongs on the analyst's follow-up
 * call, not as a submission blocker.
 */
export default function AuditIntake({ sessionId, prefillEmail }) {
  const [form, setForm] = useState({ ...FIELDS, contact_email: prefillEmail || "" });

  // Both callers resolve the address asynchronously - CheckoutSuccess waits on
  // Clerk to load the session, AuditIntakePage on a fetch - so prefillEmail is
  // reliably empty on first render and arrives later. useState reads its
  // initialiser once, which was survivable while this field could be typed into
  // and is not now that it cannot: without this the form would sit showing no
  // address and refuse to submit, with nothing the buyer could do about it.
  useEffect(() => {
    if (prefillEmail) setForm((f) => (f.contact_email === prefillEmail ? f : { ...f, contact_email: prefillEmail }));
  }, [prefillEmail]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [evidencePacks, setEvidencePacks] = useState(null);
  const [showMarketplace, setShowMarketplace] = useState(false);
  const [competitors, setCompetitors] = useState([""]);
  const [auditStage, setAuditStage] = useState(null);
  const [auditComputed, setAuditComputed] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [categoryMap, setCategoryMap] = useState({});
  const [categoryOther, setCategoryOther] = useState(false);
  const [subtypeOther, setSubtypeOther] = useState(false);

  const [fileBusy, setFileBusy] = useState(false);
  const [autoFilled, setAutoFilled] = useState(new Set());
  const [notFound, setNotFound] = useState(new Set());

  const [shippingMode, setShippingMode] = useState("type"); // "type" | "url"
  const [shippingUrlInput, setShippingUrlInput] = useState("");
  const [shippingUrlBusy, setShippingUrlBusy] = useState(false);

  useEffect(() => {
    getBenchmarkCategories().then(setCategoryMap).catch(() => setCategoryMap({}));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const setCategory = (e) => {
    const v = e.target.value;
    if (v === OTHER) {
      setCategoryOther(true);
      setForm((f) => ({ ...f, business_category: "", business_subtype: "" }));
    } else {
      setCategoryOther(false);
      setForm((f) => ({ ...f, business_category: v, business_subtype: "" }));
    }
  };
  const setSubtype = (e) => {
    const v = e.target.value;
    if (v === OTHER) {
      setSubtypeOther(true);
      setForm((f) => ({ ...f, business_subtype: "" }));
    } else {
      setSubtypeOther(false);
      setForm((f) => ({ ...f, business_subtype: v }));
    }
  };

  const addCompetitor = () => setCompetitors((c) => (c.length < 5 ? [...c, ""] : c));
  const removeCompetitor = (i) => setCompetitors((c) => c.filter((_, idx) => idx !== i));
  const updateCompetitor = (i, val) =>
    setCompetitors((c) => c.map((v, idx) => (idx === i ? val : v)));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Checked here as well as server-side: the backend rejects an oversized
    // file too, but only after it has finished uploading. On a large file
    // that is a long wait for a refusal the browser already knew about.
    const MAX_MB = 15;
    if (file.size > MAX_MB * 1_000_000) {
      toast.error(
        `That file is ${(file.size / 1_000_000).toFixed(0)}MB. Please upload one under ${MAX_MB}MB, or enter the numbers by hand.`
      );
      e.target.value = "";
      return;
    }
    if (!/\.(pdf|xlsx|csv)$/i.test(file.name)) {
      toast.error("Upload a PDF, .xlsx or .csv file.");
      e.target.value = "";
      return;
    }
    try {
      setFileBusy(true);
      const { extracted, not_found } = await extractIntakeFinancials(file, sessionId);
      // Compute the filled set as a pure value first - mutating an external
      // Set as a side effect inside a setState updater is unsafe (React can
      // invoke an updater more than once), which is exactly why the toast
      // below used to report 0 regardless of what actually got filled in.
      const filledEntries = Object.entries(extracted).map(([k, v]) => [
        EXTRACT_TO_FORM_KEY[k] || k,
        typeof v === "number" ? String(v) : v,
      ]);
      const filled = new Set(filledEntries.map(([formKey]) => formKey));
      setForm((f) => ({ ...f, ...Object.fromEntries(filledEntries) }));
      setAutoFilled(filled);
      setNotFound(new Set((not_found || []).map((k) => EXTRACT_TO_FORM_KEY[k] || k)));
      toast.success(`Pulled ${filled.size} field${filled.size === 1 ? "" : "s"} from ${file.name} — check the highlighted ones below.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not read that file. You can still fill the numbers in by hand.");
    } finally {
      setFileBusy(false);
      e.target.value = "";
    }
  };

  const fetchShippingPolicy = async () => {
    if (!shippingUrlInput.trim()) return;
    try {
      setShippingUrlBusy(true);
      const { summary } = await extractShippingPolicy(shippingUrlInput.trim(), sessionId);
      setForm((f) => ({ ...f, shipping_model: summary, shipping_policy_url: shippingUrlInput.trim() }));
      toast.success("Pulled a summary — check it reads right below.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not read that page. Try typing the policy in instead.");
    } finally {
      setShippingUrlBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim()) return toast.error("Business name is required");
    // No longer something the buyer can get wrong - so the message names what
    // is actually stuck, which is the session, not their typing.
    if (!/^\S+@\S+\.\S+$/.test(form.contact_email.trim()))
      return toast.error("Still loading your account details — give it a moment, or refresh the page.");
    if (!form.store_domain.trim()) return toast.error("Store domain is required");
    try {
      setBusy(true);
      const payload = { session_id: sessionId, ...form };
      for (const k of NUMERIC_FIELDS) {
        payload[k] = payload[k] === "" ? undefined : Number(payload[k]);
      }
      // An unanswered <select> sends "". The backend normalises it to
      // undeclared either way; dropping it here keeps the wire payload honest
      // about what the buyer actually told us.
      if (!payload.client_country) delete payload.client_country;
      const cleanCompetitors = competitors.map((c) => c.trim()).filter(Boolean).slice(0, 5);
      if (cleanCompetitors.length) payload.competitor_domains = cleanCompetitors;
      // Only attach evidence packs if the buyer actually supplied any signal -
      // an untouched section must submit as a plain Core Margin Audit.
      if (evidencePacks && (
        (evidencePacks.packs && evidencePacks.packs.length) ||
        evidencePacks.platform ||
        (evidencePacks.marketing_channels && evidencePacks.marketing_channels.length)
      )) {
        payload.evidence_packs = evidencePacks;
      }
      const res = await submitAuditIntake(payload);
      setDone(true);
      toast.success("Intake received.");

      // The waterfall builds automatically in the background as soon as the
      // intake lands - poll it the same way the free sweep polls its job, so
      // the buyer sees the report land without needing to refresh anything.
      if (res?.audit_job_id) {
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        const startedAt = Date.now();
        for (;;) {
          await sleep(2000);
          if (Date.now() - startedAt > 180000) {
            setAuditError("This is taking longer than expected - check back on My Account shortly.");
            break;
          }
          let job;
          try {
            job = await getAuditJob(res.audit_job_id);
          } catch {
            break;
          }
          setAuditStage(job.stage);
          if (job.status === "done" && job.report_id) {
            // The waterfall is computed, but the analyst reviews every audit
            // before a buyer sees it - so "done" here means "with the analyst
            // now", not "ready to view". No link is shown; My Account will
            // surface it once released.
            setAuditComputed(true);
            break;
          }
          if (job.status === "error") {
            setAuditError(job.error || "Something went wrong building your audit.");
            break;
          }
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not submit that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card-cream p-8 mt-8" data-testid="audit-intake-done">
        <div className="tick mb-3 flex items-center gap-2 text-[#9a3412]">
          <Check size={12} /> Intake received
        </div>
        <h2 className="font-serif-display text-2xl text-[#171717] mb-3 leading-tight">
          That's everything the analyst needs to start.
        </h2>
        <p className="font-mono-tech text-[13px] text-[#525252] leading-relaxed mb-4">
          Every figure you gave us now runs through the same measured, no-invented-numbers engine
          as the free sweep — cross-checked against your own storefront data and, if you added
          any, your named competitors'. The analyst reviews the result personally before
          anything is released to you.
        </p>
        {auditComputed ? (
          <div>
            <p className="font-mono-tech text-[12px] text-[#171717] mb-3">
              Your waterfall is built. The analyst reviews every audit before it's released — you'll see it under My Account once that's done.
            </p>
            <Link to="/account" className="btn-primary" data-testid="audit-back-to-account">
              Back to my account <ArrowRight size={14} />
            </Link>
          </div>
        ) : auditError ? (
          <p className="font-mono-tech text-[12px] text-[#9a3412]">{auditError}</p>
        ) : (
          <p className="font-mono-tech text-[12px] text-[#525252] flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#e88a51] animate-pulse" />
            Building your waterfall from these numbers{auditStage ? ` — ${auditStage}` : "…"}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="card-cream p-8 mt-8" data-testid="audit-intake">
      <div className="tick mb-3">Step 2 of 2 · Audit intake</div>
      <h2 className="font-serif-display text-2xl md:text-3xl text-[#171717] mb-3 leading-tight">
        The audit needs your numbers.
      </h2>
      <p className="font-mono-tech text-[13px] text-[#525252] leading-relaxed mb-8 max-w-2xl">
        The free sweep reads public product feeds. The audit reconciles against
        your actual P&amp;L — which nothing here can see. Every field below is
        optional except your business basics (marked with a <span className="text-[#9a3412]">*</span>);
        a gap is something the analyst fills on the call, not a reason to hold off submitting.
      </p>

      <div className="card-tint p-5 mb-8 flex items-start gap-4">
        <Upload size={16} className="text-[#9a3412] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="tick mb-1">Speed this up — optional</div>
          <p className="font-mono-tech text-[12px] text-[#525252] mb-3">
            Upload your P&amp;L or unit-economics file (PDF, Excel, or CSV) and we'll try to fill
            in the numbers below automatically. Nothing is saved until you review and submit —
            check every auto-filled field before sending.
          </p>
          <label className="btn-secondary inline-flex cursor-pointer" data-testid="pnl-upload-label">
            {fileBusy ? <><Loader2 size={14} className="animate-spin" /> Reading…</> : <>Upload file</>}
            <input type="file" accept=".pdf,.xlsx,.csv" className="hidden" onChange={handleFileUpload} disabled={fileBusy} data-testid="pnl-upload-input" />
          </label>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6 max-w-2xl">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business name" required><input className="input-vault" value={form.business_name} onChange={set("business_name")} /></Field>
          {/* Not editable, and not merely as a convenience. The audit is owned
              by the account, and the server now takes this address from the
              signed-in session rather than from this field - so an editable box
              here would invite a buyer to type an address that silently loses
              them access to what they just bought. Shown rather than hidden
              because they should see which account the audit will belong to
              while they can still sign in as a different one. */}
          <Field label="Contact email" required>
            <div className="input-vault flex items-center justify-between gap-3">
              <span className="truncate">{form.contact_email || "—"}</span>
              <span className="tick text-[#525252] shrink-0">Your account</span>
            </div>
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Store domain" required><input className="input-vault" placeholder="yourbrand.com" value={form.store_domain} onChange={set("store_domain")} spellCheck={false} /></Field>
        </div>
        <GroupHeader tag="Category — optional" title="What kind of business this is"
          subtitle="Selects which industry benchmarks this audit cites - a menswear shirting brand shouldn't be measured against a generic retail average. Falls back to a general online-retail benchmark if left blank." />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category">
            {categoryOther ? (
              <div className="flex items-center gap-2">
                <input className="input-vault flex-1" placeholder="Name your category" value={form.business_category} onChange={set("business_category")} />
                <button type="button" className="tick text-[#9a3412] shrink-0" onClick={() => { setCategoryOther(false); setForm((f) => ({ ...f, business_category: "" })); }}>
                  Choose from list
                </button>
              </div>
            ) : (
              <select className="input-vault" value={form.business_category} onChange={setCategory}>
                <option value="">Select…</option>
                {Object.keys(categoryMap).map((c) => <option key={c} value={c}>{c}</option>)}
                <option value={OTHER}>Other (type your own)</option>
              </select>
            )}
          </Field>
          <Field label="Subtype" hint="Optional">
            {subtypeOther ? (
              <div className="flex items-center gap-2">
                <input className="input-vault flex-1" placeholder="Name your subtype" value={form.business_subtype} onChange={set("business_subtype")} />
                <button type="button" className="tick text-[#9a3412] shrink-0" onClick={() => { setSubtypeOther(false); setForm((f) => ({ ...f, business_subtype: "" })); }}>
                  Choose from list
                </button>
              </div>
            ) : (
              <select className="input-vault" value={form.business_subtype} onChange={setSubtype} disabled={!form.business_category && !categoryOther}>
                <option value="">Select…</option>
                {(categoryMap[form.business_category] || []).map((s) => <option key={s} value={s}>{s}</option>)}
                <option value={OTHER}>Other (type your own)</option>
              </select>
            )}
          </Field>
        </div>

        <GroupHeader tag="The basis" title="What period this covers"
          subtitle="A single representative month is fine. More months (or noting seasonality) makes the audit sharper." />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field
            label="Where does the business primarily trade?"
            hint="This decides which market benchmarks apply. We only hold comparators for Australian online-only brands, so if you trade elsewhere the audit reports your own numbers and leaves the category comparisons out rather than using ones that do not describe you."
          >
            <select className="input-vault" value={form.client_country} onChange={set("client_country")}>
              <option value="">Select…</option>
              {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
          </Field>
          <Field label="Currency of the figures below" highlight={autoFilled.has("currency") ? "auto" : undefined}>
            <select className="input-vault" value={form.currency} onChange={set("currency")}>
              <option value="">Select…</option>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Months this data reflects" hint="e.g. 1 for a single representative month, 12 for a full year" highlight={autoFilled.has("reporting_period_months") ? "auto" : notFound.has("reporting_period_months") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 1" value={form.reporting_period_months} onChange={set("reporting_period_months")} />
          </Field>
          <Field label="Monthly gross revenue" highlight={autoFilled.has("monthly_gross_revenue") ? "auto" : notFound.has("monthly_gross_revenue") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 814200" value={form.monthly_gross_revenue} onChange={set("monthly_gross_revenue")} />
          </Field>
          <Field label="Monthly order volume (gross orders)" highlight={autoFilled.has("monthly_order_volume") ? "auto" : notFound.has("monthly_order_volume") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 118" value={form.monthly_order_volume} onChange={set("monthly_order_volume")} />
          </Field>
        </div>

        <GroupHeader tag="CM1 · Unit economics" title="Product cost, before anything else"
          subtitle="What it costs to make and land one order, before shipping, returns, or marketing touch it." />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Average order value (AOV)" highlight={autoFilled.has("aov") ? "auto" : notFound.has("aov") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 6900" value={form.aov} onChange={set("aov")} />
          </Field>
          <Field label="COGS as % of AOV" hint="Raw material + manufacturing/tailoring, blended" highlight={autoFilled.has("cogs_pct_of_aov") ? "auto" : notFound.has("cogs_pct_of_aov") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 47" value={form.cogs_pct_of_aov} onChange={set("cogs_pct_of_aov")} />
          </Field>
        </div>
        {/* Decides whether COGS is charged on net or gross sales, which is
            worth 6.6 points of CM1 at a 13% return rate. It was assumed
            silently until 20 Aug; a number that large should be answered by
            the person who knows, not defaulted by us. */}
        <Field label="Returned products are" hint="Resold items cost you the goods once; discarded ones cost you the goods on every order shipped">
          <select className="input-vault" data-testid="intake-returns-restocked"
                  value={form.returns_restocked} onChange={set("returns_restocked")}>
            <option value="1">Put back on the shelf and resold</option>
            <option value="0">Discarded or written off</option>
          </select>
        </Field>
        <Field label="Category / product mix" hint="Only if margin differs meaningfully by category">
          <input className="input-vault" placeholder="e.g. 70% shirts/trousers by volume, 30% blazers" value={form.category_mix_notes} onChange={set("category_mix_notes")} />
        </Field>

        <GroupHeader tag="CM2 · Fulfillment & returns" title="What it costs to actually deliver and take back an order"
          subtitle="This is usually where a P&L looks healthier than the business actually is." />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Return rate (%)" highlight={autoFilled.has("return_rate_pct") ? "auto" : notFound.has("return_rate_pct") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 17.5" value={form.return_rate_pct} onChange={set("return_rate_pct")} />
          </Field>
          <Field label="Cost per return" hint="Onward shipping (sunk) + reverse shipping/restocking" highlight={autoFilled.has("cost_per_return") ? "auto" : notFound.has("cost_per_return") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 590" value={form.cost_per_return} onChange={set("cost_per_return")} />
          </Field>
          <Field label="Onward shipping cost per order" highlight={autoFilled.has("onward_shipping_cost") ? "auto" : notFound.has("onward_shipping_cost") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 245" value={form.onward_shipping_cost} onChange={set("onward_shipping_cost")} />
          </Field>
          <Field label="Packaging cost per order" highlight={autoFilled.has("packaging_cost") ? "auto" : notFound.has("packaging_cost") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 60" value={form.packaging_cost} onChange={set("packaging_cost")} />
          </Field>
          <Field label="Payment gateway fee (%)" highlight={autoFilled.has("payment_gateway_fee_pct") ? "auto" : notFound.has("payment_gateway_fee_pct") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 2" value={form.payment_gateway_fee_pct} onChange={set("payment_gateway_fee_pct")} />
          </Field>
          <Field label="3PL / pick-pack fee per order" hint="Leave blank if bundled into shipping above" highlight={autoFilled.has("fulfillment_3pl_fee") ? "auto" : notFound.has("fulfillment_3pl_fee") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" value={form.fulfillment_3pl_fee} onChange={set("fulfillment_3pl_fee")} />
          </Field>
        </div>

        <Field label="Shipping policy shown to customers" hint="Your published policy, not the cost - e.g. free over a threshold">
          <div className="flex gap-2 mb-2">
            <button type="button" onClick={() => setShippingMode("type")} className={`tick px-3 py-1.5 rounded ${shippingMode === "type" ? "bg-[#171717] text-[#f5f2ed]" : "bg-[#eeeae2] text-[#525252]"}`}>Type it in</button>
            <button type="button" onClick={() => setShippingMode("url")} className={`tick px-3 py-1.5 rounded flex items-center gap-1 ${shippingMode === "url" ? "bg-[#171717] text-[#f5f2ed]" : "bg-[#eeeae2] text-[#525252]"}`}><LinkIcon size={11} /> Pull from a URL</button>
          </div>
          {shippingMode === "type" ? (
            <input className="input-vault" placeholder="e.g. free over A$99, flat A$12 under, 3PL in Melbourne" value={form.shipping_model} onChange={set("shipping_model")} />
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input className="input-vault flex-1" placeholder="https://yourbrand.com/pages/shipping" value={shippingUrlInput} onChange={(e) => setShippingUrlInput(e.target.value)} spellCheck={false} />
                <button type="button" onClick={fetchShippingPolicy} disabled={shippingUrlBusy} className="btn-secondary shrink-0 disabled:opacity-60">
                  {shippingUrlBusy ? <Loader2 size={14} className="animate-spin" /> : "Fetch"}
                </button>
              </div>
              {form.shipping_model && (
                <textarea rows={2} className="input-vault resize-y" value={form.shipping_model} onChange={set("shipping_model")} />
              )}
            </div>
          )}
        </Field>

        <GroupHeader tag="CM3 · Marketing" title="What it costs to acquire the order in the first place"
          subtitle="Product economics can be healthy while acquisition economics quietly sink the business." />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Monthly marketing / ad spend" highlight={autoFilled.has("monthly_marketing_spend") ? "auto" : notFound.has("monthly_marketing_spend") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 268686" value={form.monthly_marketing_spend} onChange={set("monthly_marketing_spend")} />
          </Field>
          <Field label="Blended ROAS" hint="Revenue ÷ ad spend, if you track it directly" highlight={autoFilled.has("blended_roas") ? "auto" : notFound.has("blended_roas") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 1.5" value={form.blended_roas} onChange={set("blended_roas")} />
          </Field>
          <Field label="% of revenue paid-attributed" hint="vs. organic, referral, or repeat" highlight={autoFilled.has("paid_attributed_revenue_pct") ? "auto" : notFound.has("paid_attributed_revenue_pct") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 60" value={form.paid_attributed_revenue_pct} onChange={set("paid_attributed_revenue_pct")} />
          </Field>
          <Field label="Repeat purchase rate (%)" hint="Within 6 months, if you track it" highlight={autoFilled.has("repeat_purchase_rate_pct") ? "auto" : notFound.has("repeat_purchase_rate_pct") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 27" value={form.repeat_purchase_rate_pct} onChange={set("repeat_purchase_rate_pct")} />
          </Field>
          {/* Without this the audit derives orders-per-customer from the repeat
              rate, which makes LTV:CAC an algebraic identity rather than a
              measurement - see analysis.customer_economics(). Supplying it is
              what turns that ratio into a real number. */}
          <Field label="Orders per customer, 12 months" hint="Average across all customers - makes LTV:CAC a real measurement rather than a derived one" highlight={autoFilled.has("avg_orders_per_customer_12m") ? "auto" : notFound.has("avg_orders_per_customer_12m") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 1.8" value={form.avg_orders_per_customer_12m} onChange={set("avg_orders_per_customer_12m")} />
          </Field>
        </div>

        <div className="pt-2 border-t border-[#ddd8ce]">
          <button
            type="button"
            onClick={() => setShowMarketplace((v) => !v)}
            className="flex items-center gap-2 tick text-[#9a3412] pt-4"
            data-testid="toggle-marketplace"
          >
            {showMarketplace ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            CM4 · Marketplace revenue — optional, skip if you sell direct-to-site only
          </button>
        </div>
        {showMarketplace && (
          <div className="space-y-4 pl-4 border-l-2 border-[#ddd8ce]">
            <label className="flex items-center gap-2 font-mono-tech text-[12px] text-[#171717]">
              <input type="checkbox" checked={form.sells_on_marketplaces} onChange={setBool("sells_on_marketplaces")} />
              We sell on one or more marketplaces in addition to our own site
            </label>
            {form.sells_on_marketplaces && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Which marketplaces" hint="e.g. Amazon, Myntra, Ajio, Noon">
                  <input className="input-vault" value={form.marketplace_names} onChange={set("marketplace_names")} />
                </Field>
                <Field label="% of revenue from marketplaces">
                  <input type="number" step="any" className="input-vault" value={form.marketplace_revenue_share_pct} onChange={set("marketplace_revenue_share_pct")} />
                </Field>
                <Field label="Blended marketplace commission (%)">
                  <input type="number" step="any" className="input-vault" value={form.marketplace_commission_pct} onChange={set("marketplace_commission_pct")} />
                </Field>
                <Field label="Marketplace-specific ad spend / month">
                  <input type="number" step="any" className="input-vault" value={form.marketplace_ad_spend} onChange={set("marketplace_ad_spend")} />
                </Field>
              </div>
            )}
          </div>
        )}

        <GroupHeader tag="Fixed overhead" title="What runs regardless of order volume"
          subtitle="Salaries, rent, software, and one-off costs spread over time - needed to get from contribution margin to a real net figure." />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Monthly fixed overhead" hint="Salaries + rent/warehouse + platform/software, combined" highlight={autoFilled.has("monthly_fixed_overhead") ? "auto" : notFound.has("monthly_fixed_overhead") ? "missing" : undefined}>
            <input type="number" step="any" className="input-vault" placeholder="e.g. 27167" value={form.monthly_fixed_overhead} onChange={set("monthly_fixed_overhead")} />
          </Field>
          <Field label="Amortized one-off costs" hint="e.g. photoshoot or site build, spread over its useful life">
            <input className="input-vault" placeholder="e.g. 2.3L photoshoot amortised over 24mo" value={form.amortized_capex_notes} onChange={set("amortized_capex_notes")} />
          </Field>
        </div>

        {/* Split of the line above. Optional, but every other cost in this form
            already arrives itemised - this is the only one that does not, so it
            is the only one the audit cannot be specific about when it turns out
            to be the binding constraint. */}
        <p className="mt-5 mb-3 text-[11px] tracking-[0.14em] uppercase text-ink/45 font-mono">
          Optional split - lets the audit name which fixed cost to attack, not just "overhead"
        </p>
        <div className="grid sm:grid-cols-4 gap-4">
          <Field label="Salaries" hint="Incl. founder if paid">
            <input type="number" step="any" className="input-vault" placeholder="e.g. 38000" value={form.overhead_salaries} onChange={set("overhead_salaries")} />
          </Field>
          <Field label="Rent / warehouse">
            <input type="number" step="any" className="input-vault" placeholder="e.g. 14000" value={form.overhead_rent} onChange={set("overhead_rent")} />
          </Field>
          <Field label="Software / platform">
            <input type="number" step="any" className="input-vault" placeholder="e.g. 9000" value={form.overhead_software} onChange={set("overhead_software")} />
          </Field>
          <Field label="Everything else">
            <input type="number" step="any" className="input-vault" placeholder="e.g. 7000" value={form.overhead_other} onChange={set("overhead_other")} />
          </Field>
        </div>

        <GroupHeader tag="Make this audit decision-grade — optional" title="Add evidence for the decisions you want us to assess"
          subtitle="The core audit above diagnoses your margin. These optional packs let it defensibly forecast the effect of changing price or marketing spend — without them, those recommendations are marked not evaluated rather than guessed." />
        <EvidencePacks sessionId={sessionId} onChange={setEvidencePacks} />

        <GroupHeader tag="Competitors — optional" title="Up to 5 competitors to read alongside your own numbers"
          subtitle="Each domain gets the same public catalog read your free sweep runs (no extra cost to you) — used to show what matching a competitor's pricing or discount posture would actually cost, against your real margin." />
        <div className="space-y-3">
          {competitors.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                className="input-vault flex-1"
                placeholder="competitorbrand.com"
                value={c}
                onChange={(e) => updateCompetitor(i, e.target.value)}
                spellCheck={false}
                data-testid={`competitor-domain-${i}`}
              />
              {competitors.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCompetitor(i)}
                  className="tick text-[#9a3412] px-2"
                  aria-label="Remove competitor"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          {competitors.length < 5 && (
            <button type="button" onClick={addCompetitor} className="tick text-[#9a3412]" data-testid="add-competitor">
              + Add another competitor ({competitors.length}/5)
            </button>
          )}
        </div>

        <GroupHeader tag="About the business" title="What the numbers alone don't explain"
          subtitle="A policy page tells the analyst more about what's driving a return rate than the rate itself does." />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Return policy URL">
            <input type="url" className="input-vault" placeholder="https://yourbrand.com/returns" value={form.return_policy_url} onChange={set("return_policy_url")} />
          </Field>
          <Field label="Shipping policy URL">
            <input type="url" className="input-vault" placeholder="https://yourbrand.com/shipping" value={form.shipping_policy_url} onChange={set("shipping_policy_url")} />
          </Field>
        </div>
        <Field label="Warranty / guarantee policy URL" hint="If you publish one">
          <input type="url" className="input-vault" placeholder="https://yourbrand.com/warranty" value={form.warranty_or_guarantee_url} onChange={set("warranty_or_guarantee_url")} />
        </Field>
        <Field label="How would you describe your positioning?" hint="Premium, value, niche - and who you consider your real competition, whether or not they're who the free sweep compared you against">
          <textarea rows={3} className="input-vault resize-y" value={form.brand_positioning_notes} onChange={set("brand_positioning_notes")} />
        </Field>
        <Field label="Who is your target customer?" hint="Age range, spend behaviour, occasion - whatever you'd tell a new hire in one paragraph">
          <textarea rows={3} className="input-vault resize-y" value={form.target_customer_notes} onChange={set("target_customer_notes")} />
        </Field>

        <div>
          <label className="tick block mb-2" htmlFor="ai-pnl">
            If the analyst needs your source P&amp;L later, where would it come from?
          </label>
          <select id="ai-pnl" data-testid="intake-pnl" className="input-vault"
                  value={form.pnl_format} onChange={set("pnl_format")}>
            <option value="">Select…</option>
            {PNL_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <p className="font-mono-tech text-[11px] text-[#525252] mt-2">
            Just so the analyst knows what to expect on the call — don't export or send anything now.
          </p>
        </div>

        <div>
          <label className="tick block mb-2" htmlFor="ai-question">
            The one pricing decision you want answered
          </label>
          <textarea id="ai-question" data-testid="intake-question" rows={4}
                    className="input-vault resize-y"
                    placeholder="What you'd change tomorrow if you knew the number."
                    value={form.primary_question} onChange={set("primary_question")} />
        </div>

        <div className="flex items-center justify-between gap-4 pt-2">
          <p className="font-mono-tech text-[11px] text-[#525252]">
            Stored against this purchase only.
          </p>
          <button type="submit" disabled={busy} data-testid="intake-submit"
                  className="btn-primary disabled:opacity-60 shrink-0">
            {busy ? "Submitting…" : (<>Submit intake <ArrowRight size={14} /></>)}
          </button>
        </div>
      </form>
    </div>
  );
}
